'use client';

import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import Container from '@src/components/Shared/Container';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import RenderButtons from '@src/components/Shared/RenderButtons';
import NextImage from '@src/components/Shared/NextImage';
import SectionHeader from '@src/components/Shared/SectionHeader';
import ProductBox from '@src/components/Shared/ProductBox';
import InlineSVG from '@src/components/Shared/InlineSVG';
import SocialEmbeds from '@src/components/Shared/SocialEmbeds';

import HeroValdProd from '@src/components/Sections/HeroValdProd';
import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';
import ImageTextTertiary from '@src/components/Sections/ImageTextTertiary';
import SmartSpeedGrid from '@src/components/Sections/SmartSpeedGrid';
import CallToAction from '@src/components/Sections/CallToAction';
import FeaturedProducts from '@src/components/Sections/FeaturedProducts';
import usePreviewContent from '@src/hooks/usePreviewContent';

import constructURL from '@src/utils/constructURL';

import { SmartSpeedPageClientPropTypes } from './SmartSpeedPage.types';
import SubscriptionTable from '@src/components/Shared/SubscriptionTable';

const SmartSpeedPageClient: React.FC<SmartSpeedPageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({ languageCode: page?.languageCode?.toLowerCase(), slug: `/products/smartspeed`, baseURL: '' }),
  );
  setContentType('site-page');

  return (
    <>
      <HeroValdProd
        id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
        ctfData={page?.hero?.items?.[0]}
        className='mt-[0rem]! mb-[0rem]! sm:mb-[4rem]! md:mb-[0rem]! lg:mt-[9.4rem] 2xl:mb-[0]!'
        imgClassName='mt-[2rem] md:absolute md:right-[2rem] md:w-[35rem] lg:right-[5rem] lg:relative lg:w-[40rem] lg:left-[-2rem] 2xl:left-[-10rem] 2xl:w-3xl 3xl:w-[50rem] 3xl:left-[-20rem] xsm:min-h-[42.7984rem] md:min-h-[39.7570rem] lg:min-h-[45.4297rem] 2xl:min-h-[54.5156rem] 3xl:min-h-[56.7875] md:[&>img]:mt-[9.5rem]'
        logoClassName='mt-[3rem] md:mt-[5.7rem] max-w-[27.6386rem] mb-[2.4rem] md:mt-[4.1rem] lg:max-w-[51.4rem] xsm:min-h-[5.1641rem] lg:min-h-[8.3719rem] 2xl:min-h-[9.6063rem]'
        introClassName='[&>p]:tracking-[-0.0342rem] [&>p]:mb-[1rem] [&>p]:leading-[2.7rem] [&>p]:text-[1.8rem] [&>p]:max-w-[38.4rem] md:[&>p]:text-[2rem] md:[&>p]:leading-[3rem] md:[&>p]:tracking-[-0.036rem] md:[&>p]:max-w-[38.6rem] lg:[&>p]:max-w-[48.5rem] lg:[&>p]:text-[2.4rem] lg:[&>p]:leading-[3.6rem] 2xl:[&>p]:max-w-[58.4rem] [&_a]:text-[1.8rem] md:[&_a]:text-[2rem] lg:[&_a]:text-[2.4rem]'
        headerClassName='xsm:mx-auto md:mx-[unset] md:w-[80%] md:self-start lg:self-center lg:w-[50%] 2xl:w-[50%] 3xl:w-[65%]'
      />

      <Container
        id='smartspeed-video'
        className='aspect-w-16 aspect-h-9 mt-[-4rem] w-full py-[2.4rem] lg:mt-[unset] lg:py-[5.1rem]'
        background='bg-background-primary'
      >
        <SocialEmbeds
          className='overflow-hidden rounded-[0.6rem] shadow-[0px_5px_15px_rgba(0,0,0,0.35)]'
          data={page?.smartspeedPromo?.items?.[0]}
        />
      </Container>

      <div id={page?.everyAspect?.items?.[0]?.scrollTo?.slice(1)} className='4xl:bg-background-tertiary w-full'>
        <ImageTextSecondary
          reversed
          title={page?.everyAspect?.items?.[0]?.title}
          imgURL={page?.everyAspect?.items?.[0]?.mediaPrimary?.url}
          imgFirstClassName='mt-[-1rem] md:mt-[-2rem] lg:mt-[unset]'
          childrenElements={
            <>
              <RenderBodyCopy
                className='2xl:max-w-[78.2rem]'
                bodyCopy={page?.everyAspect?.items?.[0]?.bodyCopy?.json?.content}
              />

              <RenderButtons items={page?.everyAspect?.items?.[0]?.buttonCollection?.items} />
            </>
          }
        />
      </div>

      <ImageTextSecondary
        id={page?.minusTheFrustration?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.minusTheFrustration?.items?.[0]?.preTitle}
        title={page?.minusTheFrustration?.items?.[0]?.title}
        imgURL={page?.minusTheFrustration?.items?.[0]?.mediaPrimary?.url}
        titleClassName='max-w-[50rem]'
        imgFirstClassName='mt-[-1rem] md:mt-[-2rem] lg:mt-[unset]'
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.minusTheFrustration?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.minusTheFrustration?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <ImageTextSecondary
        reversed
        id={page?.allFromOneApp?.items?.[0]?.scrollTo?.slice(1)}
        className='overflow-visible! lg:py-[0]! lg:pt-[3.6rem]'
        background='bg-background-tertiary'
        pretitle={page?.allFromOneApp?.items?.[0]?.preTitle}
        title={page?.allFromOneApp?.items?.[0]?.title}
        titleClassName='2xl:max-w-[60rem]'
        imgURL={page?.allFromOneApp?.items?.[0]?.mediaPrimary?.url}
        objectFit='contain'
        imgFirstClassName='mt-[-1rem] md:mt-[-2rem] lg:mt-[unset]'
        imgSecondClassName='w-[50%]'
        textContainerClassName='pb-[3.3rem]'
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.allFromOneApp?.items?.[0]?.bodyCopy?.json?.content}
            />

            <RenderButtons items={page?.allFromOneApp?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <Container id={page?.wideRangeOfTests?.items?.[0]?.scrollTo?.slice(1)} className='md:flex md:flex-col lg:mb-[0]!'>
        <NextImage
          className='mb-[2.4rem] w-full md:order-1 md:mx-auto md:mb-[unset] lg:w-[60%]'
          src={page?.wideRangeOfTests?.items?.[0]?.mediaPrimary?.url}
          alt={page?.wideRangeOfTests?.items?.[0]?.mediaPrimary?.description ?? 'VALD Image'}
          objectFit='contain'
          aspectRatio={
            page?.wideRangeOfTests?.items?.[0]?.mediaPrimary?.width /
            page?.wideRangeOfTests?.items?.[0]?.mediaPrimary?.height
          }
          width={page?.wideRangeOfTests?.items?.[0]?.mediaPrimary?.width} // Set actual width
        />

        <SectionHeader
          className='justify-center md:mb-[1.8rem] lg:pb-[unset]'
          pretitle={page?.wideRangeOfTests?.items?.[0]?.preTitle}
          title={page?.wideRangeOfTests?.items?.[0]?.title}
          titleClassName='max-w-[71.6rem] text-[2.8rem] xl:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light'
          align='center'
        >
          <>
            <RenderBodyCopy
              className='lg:max-w-[78.2rem]'
              bodyCopy={page?.wideRangeOfTests?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons
              className='justify-center'
              items={page?.wideRangeOfTests?.items?.[0]?.buttonCollection?.items}
            />
          </>
        </SectionHeader>
      </Container>

      <ImageTextTertiary
        reversed
        id={page?.upInLights?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-background-dark pb-[1rem] pt-[4rem] lg:pb-[0]'
        title={page?.upInLights?.items?.[0]?.title}
        titleClassName='md:max-w-[43rem] text-body-copy--light'
        imgURL={page?.upInLights?.items?.[0]?.mediaPrimary?.url}
        imgFirstClassName='mx-[5rem] md:mx-[20rem]'
        imgAlt={page?.upInLights?.items?.[0]?.mediaPrimary?.description ?? 'Vald image'}
        aspectRatio={
          page?.upInLights?.items?.[0]?.mediaPrimary?.width / page?.upInLights?.items?.[0]?.mediaPrimary?.height
        }
        width={page?.upInLights?.items?.[0]?.mediaPrimary?.width}
        imgSecondClassName='mt-[-10rem] lg:max-h-[65rem] 2xl:max-h-[60rem]'
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='text-body-copy--light 2xl:max-w-[58.2rem]'
              bodyCopy={page?.upInLights?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons className='justify-center' items={page?.upInLights?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <Container id={page?.smartSpeedRange?.items?.[0]?.scrollTo?.slice(1)} className='mb-[2rem]! xl:mb-[-6rem]!'>
        <SectionHeader
          className='mt-[3rem] justify-center pb-[3.3rem] md:mb-[3.4rem] lg:pb-[unset]'
          childrenClassName='max-w-[111rem]! xl:max-w-[111rem]! w-full'
          pretitle={page?.smartSpeedRange?.items?.[0]?.preTitle}
          title={page?.smartSpeedRange?.items?.[0]?.title}
          titleClassName='text-[2.8rem] xl:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light xl:max-w-[45rem] inline-block'
          align='center'
        >
          <>
            <SocialEmbeds
              className='overflow-hidden rounded-[0.6rem] shadow-[0px_5px_15px_rgba(0,0,0,0.35)]'
              data={page?.smartSpeedRange?.items?.[0]?.extraAssetsCollection?.items?.[0]}
            />
            <RenderBodyCopy
              className='mt-[1rem] inline-block md:max-w-[59rem]'
              bodyCopy={page?.smartSpeedRange?.items?.[0]?.bodyCopy?.json?.content}
            />

            <RenderButtons
              className='justify-center'
              items={page?.smartSpeedRange?.items?.[0]?.buttonCollection?.items}
            />
          </>
        </SectionHeader>
      </Container>

      <Container
        id={page?.smartspeedPlus?.items?.[0]?.scrollTo?.slice(1)}
        className='2x:mt-[-10rem] mb-[0]! flex flex-col items-center justify-center lg:flex-row 2xl:mb-[0]!'
      >
        <ProductBox
          className='lg:max-w-[55rem]'
          product={page?.smartspeedPlus?.items?.[0]}
          buttonClassName='[&>div>[data-pseudo]]:locale-fr:before:px-[2rem]'
        />
        <NextImage
          className='w-full md:w-[50%] md:max-w-4xl'
          src={page?.smartspeedPlus?.items?.[0]?.mediaPrimary?.url}
          alt={page?.smartspeedPlus?.items?.[0]?.mediaPrimary?.description ?? 'VALD Image'}
          objectFit='contain'
          aspectRatio={
            page?.smartspeedPlus?.items?.[0]?.mediaPrimary?.width /
            page?.smartspeedPlus?.items?.[0]?.mediaPrimary?.height
          }
          width={page?.smartspeedPlus?.items?.[0]?.mediaPrimary?.width} // Set actual width
        />
      </Container>
      <SmartSpeedGrid smartSpeed={page?.smartspeedPlus?.items?.[0]?.extraAssetsCollection?.items} />

      <div
        id={page?.smartspeedDash?.items?.[0]?.scrollTo?.slice(1)}
        className='bg-background-primary 3xl:py-[5rem]! py-[2rem]'
      >
        <Container className='flex flex-col-reverse xl:mb-[0]! xl:flex-row xl:gap-[2rem]'>
          <InlineSVG
            className='mx-auto md:hidden'
            src={page?.smartspeedDash?.items?.[0]?.mediaSecondary?.url}
            renderAt={['mobile']}
          />
          <InlineSVG
            className='hidden md:block'
            src={page?.smartspeedDash?.items?.[0]?.mediaPrimary?.url}
            renderAt={['desktop', 'tablet']}
          />

          <ProductBox
            className='text-body-copy--light hidden bg-[#303437]! p-[2rem] lg:block lg:max-w-[47rem]! xl:self-center'
            headingClassName='text-body-copy--light'
            buttonClassName='[&>div>a[data-pseudo]]:text-primary!'
            product={page?.smartspeedDash?.items?.[0]}
          />

          <SectionHeader
            className='mt-[3rem] justify-center pb-[3.3rem] md:mb-[3.4rem] lg:hidden lg:pb-[unset]'
            pretitle={page?.smartspeedDash?.items?.[0]?.preTitle}
            title={page?.smartspeedDash?.items?.[0]?.title}
            titleClassName='text-[2.8rem] xl:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light text-body-copy--light'
            align='left'
          >
            <>
              <RenderBodyCopy
                className='text-body-copy--light md:max-w-[60rem]'
                bodyCopy={page?.smartspeedDash?.items?.[0]?.bodyCopy?.json?.content}
              />

              <RenderButtons
                className='justify-left [&>div>a[data-pseudo]]:text-primary!'
                items={page?.smartspeedDash?.items?.[0]?.buttonCollection?.items}
              />
            </>
          </SectionHeader>
        </Container>
      </div>

      <Container>
        <ProductBox
          className='mt-[8rem] mb-[5.2rem] md:max-w-[45rem] lg:max-w-[58.1rem]!'
          product={page?.smartspeedAccessories?.items?.[0]}
        />

        <div className='md:flex md:flex-col'>
          <InlineSVG
            className='mb-[2.4rem] max-w-[24.7rem] md:mt-auto md:mb-[0] md:hidden lg:mb-[-5rem]'
            src={page?.smartJump?.items?.[0]?.mediaPrimary?.url}
            renderAt={['mobile']}
          />
          <div className='md:flex md:items-center md:justify-center'>
            <NextImage
              className='mb-[2.4rem] md:relative md:bottom-[-4rem] md:order-1 md:mb-[0] md:w-[50%] lg:bottom-[-2rem]'
              src={page?.smartJump?.items?.[0]?.mediaSecondary?.url}
              alt={page?.smartJump?.items?.[0]?.mediaSecondary?.description ?? 'VALD Image'}
              aspectRatio={
                page?.smartJump?.items?.[0]?.mediaSecondary?.width / page?.smartJump?.items?.[0]?.mediaSecondary?.height
              }
              width={page?.smartJump?.items?.[0]?.mediaSecondary?.width}
            />

            <div className='md:w-[50%]'>
              <InlineSVG
                className='3xl:max-w-[35rem] mb-[2.4rem] hidden max-w-[24.7rem] md:block lg:w-[60%] lg:max-w-[30rem]'
                src={page?.smartJump?.items?.[0]?.mediaPrimary?.url}
                renderAt={['desktop', 'tablet']}
              />

              <RenderBodyCopy bodyCopy={page?.smartJump?.items?.[0]?.bodyCopy?.json?.content} />

              <RenderButtons items={page?.smartJump?.items?.[0]?.buttonCollection?.items} />
            </div>
          </div>
        </div>

        <hr className='border-border my-[2.4rem] md:mt-[4.8rem] md:mb-[-2.4rem] lg:mt-[8rem] lg:mb-[2.4rem]' />

        <Container id={page?.smartJump?.items?.[0]?.scrollTo?.slice(1)} className='px-[0]!'>
          <div className='grid grid-cols-1 gap-[1.6rem] md:grid-cols-2 lg:grid-cols-3'>
            {page?.smartJump?.items?.[0]?.extraAssetsCollection?.items?.map((item) => {
              return (
                <div
                  key={`${item?.sys?.id}-smart-jump-accessories`}
                  className='bg-background-tertiary rounded-3xl px-[2rem] pb-[2rem]'
                >
                  <NextImage
                    className='3xl:max-h-[30rem] max-w-[45rem] md:mb-[2.4rem] lg:mx-auto'
                    src={item?.mediaPrimary?.url}
                    alt={item?.mediaPrimary?.description ?? 'smartjump accesories'}
                    aspectRatio={item?.mediaPrimary?.width / item?.mediaPrimary?.height}
                    width={item?.mediaPrimary?.width}
                  />
                  <h3
                    className='text-primary mb-[1.8rem] text-[2.2rem] leading-none font-normal md:text-[2.2rem] 2xl:text-[3.2rem]'
                    style={{
                      textShadow: 'rgb(143, 66, 0, 0.2) 1px 0px 0px, rgb(143, 66, 0, 0.2) 0.540302px 0.841471px 0px',
                    }}
                  >
                    {item?.title}
                  </h3>
                  <RenderBodyCopy bodyCopy={item.bodyCopy?.json?.content} />
                </div>
              );
            })}
          </div>
        </Container>

        <Container
          id={page?.valdSubscription?.items?.[0]?.scrollTo?.slice(1)}
          background='bg-[#F9F9F9]'
          className='mt-[0]! py-[8rem]'
        >
          <SectionHeader
            className='mb-[2.4rem] lg:mb-[unset]'
            childrenClassName='w-full'
            pretitle={page?.valdSubscription?.items?.[0]?.preTitle}
            title={page?.valdSubscription?.items?.[0]?.title}
            titleClassName='text-[2.8rem] xl:text-[4.8rem] [&>span]:inline-block mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light xl:max-w-[60rem] inline-block'
            align='center'
          >
            <RenderButtons
              items={page?.valdSubscription?.items?.[0]?.buttonCollection?.items}
              className='mt-[1rem]! justify-center'
            />
          </SectionHeader>
          <SubscriptionTable
            headerImgL='w-[11rem] lg:w-[13rem] lg:mt-[-3.35rem]'
            headerImgR='w-[8rem] lg:w-[11rem] lg:mt-[-5rem]'
            data={page?.valdSubscription?.items}
          />
        </Container>

        <FeaturedProducts className='[&>header>h2]:max-w-[60rem]' data={page?.valdSystems?.items?.[0]} />

        <CallToAction
          attr='call-to-action-component'
          id={page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
          title={page?.callToAction?.items?.[0]?.title}
          bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
          buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
        />
      </Container>
    </>
  );
};

export default SmartSpeedPageClient;
